import React, { memo, useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import {
  CopyOutlined,
  SearchOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import styles from './style.module.css';
import { DialogTranscription } from '../../../../models/CallAttempt/CallAttempt';
import { useTranscriptionSearch } from '../../../../hooks/components/useTranscriptionSearch/useTranscriptionSearch';

export interface TranscriptionViewerProps {
  transcription: DialogTranscription[];
}

const COPY_TOOLTIP_DEFAULT = 'Копировать диалог';
const COPY_TOOLTIP_SUCCESS = 'Скопировано';
const COPY_SUCCESS_DURATION_MS = 1000;
const COPY_SUCCESS_COLOR = '#52c41a';

export const TranscriptionViewer: React.FC<TranscriptionViewerProps> = memo(
  ({ transcription }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const copyFeedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );
    const [isCopySuccessActive, setIsCopySuccessActive] = useState(false);
    const {
      searchQuery,
      setSearchQuery,
      matches,
      currentMatchIndex,
      totalMatches,
      goToNextMatch,
      goToPrevMatch,
      highlightText,
    } = useTranscriptionSearch(transcription);
    const copyDialogText = transcription
      .map((entry) => {
        const speakerLabel = entry.speaker === 'Robot' ? 'Система' : 'Пациент';
        const text = entry.value ? entry.value : '—';
        return `${speakerLabel}: ${text}`;
      })
      .join('\n');

    const resetCopyFeedbackTimeout = () => {
      if (copyFeedbackTimeoutRef.current) {
        clearTimeout(copyFeedbackTimeoutRef.current);
        copyFeedbackTimeoutRef.current = null;
      }
    };

    const handleCopyDialog = async () => {
      try {
        if (!navigator.clipboard) {
          setIsCopySuccessActive(false);
          resetCopyFeedbackTimeout();
          return;
        }

        await navigator.clipboard.writeText(copyDialogText);
        resetCopyFeedbackTimeout();
        setIsCopySuccessActive(true);
        copyFeedbackTimeoutRef.current = setTimeout(() => {
          setIsCopySuccessActive(false);
          copyFeedbackTimeoutRef.current = null;
        }, COPY_SUCCESS_DURATION_MS);
      } catch {
        setIsCopySuccessActive(false);
        resetCopyFeedbackTimeout();
      }
    };

    useEffect(() => {
      const el = containerRef.current?.querySelector(
        '[data-match-active="true"] .transcription-match-active',
      );
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [currentMatchIndex]);

    useEffect(() => {
      return () => {
        if (copyFeedbackTimeoutRef.current) {
          clearTimeout(copyFeedbackTimeoutRef.current);
        }
      };
    }, []);

    const copyTooltipTitle = isCopySuccessActive
      ? COPY_TOOLTIP_SUCCESS
      : COPY_TOOLTIP_DEFAULT;
    const copyButtonClassName = isCopySuccessActive
      ? `${styles.navButton} ${styles.copyButtonSuccess}`
      : styles.navButton;

    return (
      <div className={styles.viewer}>
        <div className={styles.searchBar}>
          <SearchOutlined className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Поиск по тексту..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {totalMatches > 0 && (
            <span className={styles.matchCounter}>
              {currentMatchIndex + 1} из {totalMatches}
            </span>
          )}
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              className={styles.navButton}
              onClick={goToPrevMatch}
              disabled={totalMatches === 0}
            >
              <ArrowUpOutlined />
            </button>

            <button
              className={styles.navButton}
              onClick={goToNextMatch}
              disabled={totalMatches === 0}
            >
              <ArrowDownOutlined />
            </button>
          </div>
          <span className={styles.copyButtonWrap}>
            <Tooltip
              title={copyTooltipTitle}
              color={isCopySuccessActive ? COPY_SUCCESS_COLOR : undefined}
            >
              <button
                type="button"
                className={copyButtonClassName}
                onClick={handleCopyDialog}
                data-testid="copy-dialog-button"
                aria-label={COPY_TOOLTIP_DEFAULT}
              >
                <CopyOutlined />
              </button>
            </Tooltip>
          </span>
        </div>
        <div className={styles.transcriptScroll} ref={containerRef}>
          {transcription.map((entry, index) => {
            const isMatchActive =
              matches[currentMatchIndex]?.lineIndex === index;
            const speakerLabel =
              entry.speaker === 'Robot' ? 'Система' : 'Пациент';
            const speakerClass =
              entry.speaker === 'Robot'
                ? styles.speaker
                : `${styles.speaker} ${styles.speakerPatient}`;
            const textContent = entry.value
              ? highlightText(entry.value, index)
              : '—';
            const textClass = entry.value ? styles.lineText : styles.emptyLine;

            return (
              <div
                key={entry.id}
                className={styles.transcriptLine}
                data-match-active={isMatchActive ? 'true' : undefined}
              >
                <span className={speakerClass}>{speakerLabel}:</span>
                <span className={textClass}>{textContent}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

TranscriptionViewer.displayName = 'TranscriptionViewer';
